import React, { useLayoutEffect, useState } from 'react'
import { Tooltip } from 'antd'
import './org-dashboard.scss'

type ClampedLineProps = {
    /** The whole line. The tooltip shows it when the visible line is cut short. */
    text: string
    className: string
    /**
     * Draws the clamped element. `ref` and `tabIndex` belong on it: the element is a tab stop only while it is
     * actually cut short, because otherwise there is nothing for focus to reveal. An element that is focusable
     * already, such as a link, ignores `tabIndex`.
     */
    children: (props: {
        ref: React.RefCallback<HTMLElement>
        className: string
        tabIndex: number | undefined
    }) => React.ReactElement
}

/** A single line of text that ends in an ellipsis when it does not fit, with the whole text in a tooltip. */
export const ClampedLine: React.FC<ClampedLineProps> = ({ text, className, children }) => {
    const [element, setElement] = useState<HTMLElement | null>(null)
    const [truncated, setTruncated] = useState(false)

    // The column's width changes with the window and with the table's scroll width, and the text with the row.
    useLayoutEffect(() => {
        if (!element) return undefined
        const measure = () => setTruncated(element.scrollWidth > element.clientWidth)
        measure()
        if (typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [element, text])

    return (
        <Tooltip title={truncated ? text : undefined} trigger={['hover', 'focus']}>
            {children({
                ref: setElement,
                className: `org-dashboard-clamped-line ${className}`,
                tabIndex: truncated ? 0 : undefined,
            })}
        </Tooltip>
    )
}
